import { useEffect, useState } from "react";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { positionLabelKey, type Position } from "../../access/positions";
import { levelGlyph } from "./acHelpers";
import { AccessControlConfig, PreviewResponse } from "./types";

export interface PreviewViewProps {
    config: AccessControlConfig;
    warehouses: string[];
}

const PreviewView = ({ config, warehouses }: PreviewViewProps) => {
    const { t } = useTranslation();
    const [position, setPosition] = useState<string>(config.positions[0] ?? "");
    const [warehouse, setWarehouse] = useState<string>(warehouses[0] ?? "");
    const [uid, setUid] = useState<string>("");
    const [data, setData] = useState<PreviewResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!position) return;
        let alive = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({ position });
                if (warehouse) params.set("warehouse", warehouse);
                if (uid.trim()) params.set("uid", uid.trim());
                const res = await apiFetch(`/access-control/preview?${params.toString()}`);
                if (!res.ok) {
                    if (alive) setError(t("server_error"));
                    return;
                }
                const body: PreviewResponse = await res.json();
                if (alive) setData(body);
            } catch {
                if (alive) setError(t("server_error"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [position, warehouse, uid, t]);

    return (
        <div className="ac-preview">
            <div className="ac-preview-controls">
                <label className="ac-preview-field">
                    <span>{t("ac_preview_position")}</span>
                    <select value={position} onChange={e => setPosition(e.target.value)}>
                        {config.positions.map(p => (
                            <option key={p} value={p}>{t(positionLabelKey(p as Position))}</option>
                        ))}
                    </select>
                </label>
                <label className="ac-preview-field">
                    <span>{t("warehouse")}</span>
                    <select value={warehouse} onChange={e => setWarehouse(e.target.value)}>
                        <option value="">{t("status_all")}</option>
                        {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                </label>
                <label className="ac-preview-field">
                    <span>{t("ac_preview_uid")}</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder={t("ac_preview_uid_placeholder")}
                        value={uid}
                        onChange={e => setUid(e.target.value)}
                    />
                </label>
            </div>

            {loading && <div className="ac-users-loading">{t("loading")}</div>}
            {error && <p className="ac-hint ac-hint--error">{error}</p>}

            {data && (
                <div className="ac-matrix-scroll">
                    <table className="ac-matrix-table">
                        <thead>
                            <tr>
                                <th className="ac-matrix-key-col">{t("ac_key_col")}</th>
                                <th>{t("ac_preview_resolved")}</th>
                                <th>{t("ac_preview_source")}</th>
                                <th>{t("ac_preview_legacy")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {config.keys.map(k => {
                                const resolved = data.rules[k.key] ?? "none";
                                const legacy = data.legacy[k.key] ?? "none";
                                const differs = resolved !== legacy;
                                return (
                                    <tr key={k.key} className={differs ? "ac-preview-row--diff" : undefined}>
                                        <td className="ac-matrix-key-col">
                                            <div className="ac-key-label"><span>{k.label}</span></div>
                                            <code className="ac-key-code">{k.key}</code>
                                        </td>
                                        <td>{levelGlyph(resolved)}</td>
                                        <td className="ac-preview-source">{data.sources[k.key] ?? "—"}</td>
                                        <td>{levelGlyph(legacy)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PreviewView;
